// @givmo/app-admin — Settings → Security → EXPORT YOUR SECURITY HISTORY (AS-WEB7 / AS-BE20).
//
// The employer's chronology of its own security posture, downloaded as CSV over a date range.
//
// ══ ★ A CLAMP MUST NEVER BE SILENT, AND THAT IS THE HARD PART HERE ════════════════════════════
// A `from` reaching past the retention floor is CLAMPED to the floor and served — 200, a real file,
// covering a shorter period than the Owner asked for. This program bans the silent clamp (it is why
// the SSO tier write reports its own), and a clamp on a READ is the same dishonesty: the file looks
// like the whole of the requested period and is not.
//
// The server reports the effective window in RESPONSE HEADERS, so surfacing the clamp means reading
// them — which is why the download uses `responseStyle:'fields'` and keeps the raw `Response`.
//
// ⚠ AND THE HEADERS MAY NOT BE READABLE. They are non-safelisted headers on a CROSS-ORIGIN request
// (this SPA is on Firebase Hosting, the API on its own origin), so a browser hides them unless the
// backend names them in `Access-Control-Expose-Headers`. When it does not, `headers.get` answers
// `null` however correct the server was. So the clamp verdict is TRI-STATE and this screen renders:
//
//   • `true`  → the clamp notice, naming the day the file actually starts on.
//   • `false` → nothing. The range was honoured; saying so adds a line for no decision.
//   • `null`  → nothing about the window either. It must NOT say "you got what you asked for",
//               because it does not know that — asserting it on an unread header would be the
//               silent clamp with extra steps.
//
// The same tri-state governs the retention sentence: stated only on a figure the server actually
// sent, never on a number remembered here, so the two cannot drift.
//
// ══ THE OTHER TWO REFUSALS ARE TYPED AND ARE NOT CLAMPS ═══════════════════════════════════════
// An inverted range is REFUSED, never swapped (`SECURITY_AUDIT_EXPORT_RANGE_INVALID`), and a window
// holding more than the row cap is REFUSED, never truncated (`SECURITY_AUDIT_EXPORT_TOO_LARGE`) —
// because a truncated audit artifact reads as a complete chronology with a hole in it. Both arrive
// as the backend's own sentence and are rendered as such. The inverted case is additionally caught
// before the request, so the ordinary typo costs no round trip.
//
// ⚠ A REFUSAL MUST NOT PRODUCE A FILE. The typed errors arrive as `{errors:[…]}` JSON on a 400 while
// the request asked for a blob; saving that body as `.csv` would hand an Owner a file containing a
// JSON error and call it an audit trail. The transport separates them and this never saves on error.

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { isGivmoError, readBinaryMeta } from '@givmo/api-client';
import { Button, Card, CardBody, CardHeader, Field, testIdProp } from '@givmo/design-system';
import { SSO_COPY, auditClampNotice, auditRetentionNote } from './security-copy';
import { SsoErrorNote } from './sso-error-note';
import {
  auditWindowWasClamped,
  formatWindowDay,
  rangeIsOrdered,
  readAuditWindow,
  type AuditWindow,
} from './company-security-decode';
import { fetchAuditExport } from './company-security-transport';
import { blobSave } from '../reports/csv-export';
import { WRAPPING_CARD_TITLE } from './card-title';

const PREFIX = 'settings-security-audit-export';

/** What the last download reported about itself. `null` before the first one. */
interface LastExport {
  window: AuditWindow;
  clamped: boolean | null;
}

export function AuditExportCard() {
  // The native date fields' own values: '' until a day is picked, then the picked day.
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [last, setLast] = useState<LastExport | null>(null);
  const [dark, setDark] = useState(false);
  const [forbidden, setForbidden] = useState(false);

  // ⚠ THE FIELD'S VALUE IS ALREADY THE PICKED DAY — `YYYY-MM-DD` on the person's own calendar — and
  // it goes on the wire as it is. Never through a `Date`: its UTC serializer ships the previous day
  // for every viewer east of UTC while the field goes on showing the day they picked, putting a hole
  // in an artifact this card presents as complete.
  const fromDay = from === '' ? undefined : from;
  const toDay = to === '' ? undefined : to;
  // Caught before the request because the endpoint refuses rather than swapping, so the ordinary
  // typo should cost a sentence rather than a round trip and an error note.
  const ordered = fromDay === undefined || toDay === undefined || rangeIsOrdered(fromDay, toDay);

  const download = useMutation({
    mutationFn: async () => {
      const { result, error } = await fetchAuditExport({ from: fromDay, to: toDay });
      if (error !== null && error !== undefined) throw error;
      if (result === null) throw new Error(SSO_COPY.auditFailed);
      return result;
    },
    onSuccess: ({ blob, response }) => {
      setDark(false);
      setForbidden(false);
      const window = readAuditWindow(response);
      setLast({ window, clamped: auditWindowWasClamped(fromDay, window) });
      // The server's own filename when it reached us; `content-disposition` is one of the few
      // headers already CORS-exposed, so this usually does.
      blobSave(blob, readBinaryMeta(response).filename ?? SSO_COPY.auditFilename);
    },
    onError: (error) => {
      setLast(null);
      if (isGivmoError(error) && error.status === 404) setDark(true);
      if (isGivmoError(error) && error.status === 403) setForbidden(true);
    },
  });

  const withdrawn = dark || forbidden;

  return (
    <Card>
      <CardHeader
        className={WRAPPING_CARD_TITLE}
        title={SSO_COPY.auditTitle}
        subtitle={SSO_COPY.auditSubtitle}
      />
      <CardBody>
        <div className="flex flex-col gap-12" {...testIdProp(PREFIX)}>
          <p className="font-sans text-s13 font-w400 leading-h15 tracking-normal text-text-secondary">
            {SSO_COPY.auditColumnsNote}
          </p>

          {!withdrawn && (
            <>
              {/* The page's own field style (D-AS-38 (1)): the SaaS `Field` as a native date input,
                  in a 200px column — the two stack on a phone. The card's choice alone; the shared
                  `DatePicker` is unchanged. */}
              <div className="flex flex-wrap items-start gap-12">
                <div className="w-[200px] max-w-full">
                  <Field
                    type="date"
                    label={SSO_COPY.auditFromLabel}
                    value={from}
                    onChange={(event) => setFrom(event.target.value)}
                    error={!ordered}
                    testId={`${PREFIX}-from`}
                  />
                </div>
                <div className="w-[200px] max-w-full">
                  <Field
                    type="date"
                    label={SSO_COPY.auditToLabel}
                    value={to}
                    onChange={(event) => setTo(event.target.value)}
                    error={!ordered}
                    testId={`${PREFIX}-to`}
                  />
                </div>
              </div>

              {!ordered && (
                <p
                  className="font-sans text-s13 font-w500 leading-h15 tracking-normal text-danger-700"
                  {...testIdProp(`${PREFIX}-range-error`)}
                >
                  {SSO_COPY.auditRangeInverted}
                </p>
              )}

              <div>
                <Button
                  appearance="saas"
                  tone="primary"
                  // Blocked only on the range this screen can already see is wrong. Everything else
                  // is the server's to refuse, with its own sentence.
                  disabled={!ordered}
                  loading={download.isPending}
                  onClick={() => download.mutate()}
                  testId={`${PREFIX}-download`}
                >
                  {download.isPending ? SSO_COPY.auditDownloading : SSO_COPY.auditDownload}
                </Button>
              </div>
            </>
          )}

          {last !== null && <WindowReport last={last} />}

          {/* Not while withdrawn — the 404 and the 403 carry their own notices below, and the raw
              refusal beside one answers a single press two contradictory ways. */}
          {!withdrawn && download.isError && (
            <SsoErrorNote
              error={download.error}
              fallback={SSO_COPY.auditFailed}
              testId={`${PREFIX}-error`}
            />
          )}

          {dark && (
            <p
              className="font-sans text-s13 font-w400 leading-h15 tracking-normal text-text-secondary"
              {...testIdProp(`${PREFIX}-unavailable`)}
            >
              {SSO_COPY.unavailable}
            </p>
          )}

          {forbidden && (
            <p
              className="font-sans text-s13 font-w500 tracking-normal text-danger-700"
              {...testIdProp(`${PREFIX}-forbidden`)}
            >
              {SSO_COPY.forbidden}
            </p>
          )}
        </div>
      </CardBody>
    </Card>
  );
}

/**
 * What the file that just downloaded actually covered.
 *
 * ★ RENDERS ONLY WHAT THE SERVER REPORTED. A `true` clamp names the served start; a `false` says
 * nothing (the range held, and a line confirming it is a line that changes no decision); a `null`
 * says nothing either, because the report did not arrive and "you got what you asked for" is not a
 * claim this screen may make from silence. The retention sentence follows the same rule — stated on
 * a figure the server sent, never on one remembered here.
 */
function WindowReport({ last }: { last: LastExport }) {
  const servedFrom = formatWindowDay(last.window.start);

  if (last.clamped === true && servedFrom !== null) {
    return (
      <div
        className="flex flex-col gap-4 rounded-lg border border-warning-100 bg-warning-50 p-16"
        {...testIdProp(`${PREFIX}-clamped`)}
      >
        <p className="font-sans text-s13 font-w400 leading-h15 text-warning-700">
          {auditClampNotice(servedFrom, last.window.retentionDays)}
        </p>
      </div>
    );
  }

  if (last.window.retentionDays === null) return null;
  return (
    <p
      className="font-sans text-s12 font-w400 leading-h15 tracking-normal text-text-tertiary"
      {...testIdProp(`${PREFIX}-retention`)}
    >
      {auditRetentionNote(last.window.retentionDays)}
    </p>
  );
}
